/**
 * =============================================================================
 * Página: CrmPlaybookEditor (rota /crm/playbooks/:id e /crm/playbooks/new)
 * Versão: 1.0.0
 *
 * Descrição:
 *   Editor completo de um Playbook de Atividades.
 *   - Cabeçalho do playbook (nome, descrição, cor, ativo, padrão)
 *   - Lista de steps com drag-and-drop (@dnd-kit) — sort_order calculado
 *     a partir da ordem visual antes de salvar.
 *   - Cada step tem: tipo de atividade, título, canal, offset (valor + unidade),
 *     âncora (quando contar), obrigatório, SLA, descrição.
 *   - Coluna lateral com **preview de timeline** mostrando o "due_at" relativo
 *     calculado a partir do start da cadência (estimativa visual).
 *   - Salvar = chama RPC `upsert_activity_playbook` em transação atômica
 *     (delete + insert dos steps no servidor).
 *
 *   Acesso: apenas admins (a RPC valida server-side; UI esconde ações para
 *   não-admins via guard futuro do item 1.15).
 *
 * Release notes:
 *   v1.0.0 - Criação (Fase 1, item 1.13 do Playbook de Vendas).
 * =============================================================================
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  Plus,
  GripVertical,
  Trash2,
  Save,
  Phone,
  Mail,
  Calendar as CalendarIcon,
  StickyNote,
  CheckSquare,
  Clock,
  Loader2,
  MessageCircle,
  MapPin,
  ScrollText,
  HelpCircle,
  Rows3,
  Columns3,
  Check,
  Zap,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PlaybookHelpDialog } from '@/components/crm/playbook/PlaybookHelpDialog';
import { AnchorHelpPopover } from '@/components/crm/playbook/AnchorHelpPopover';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
// [v1.2.0] Aviso quando o workspace está com "Modo Playbook" desligado
// (sem esse flag, o trigger `trg_auto_start_deal_cadences` não inicia
// cadências automaticamente — ver diagnóstico do playbook).
import { InlineNotice } from '@/components/ui/inline-notice';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { StepScriptSheet } from '@/components/crm/playbook/StepScriptSheet';
// [v1.1.0] Picker de variáveis para o TÍTULO do step (subset de 15 tokens)
import { VariablePicker } from '@/components/crm/playbook/VariablePicker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Variable } from 'lucide-react';
import { renderScriptAsText, PREVIEW_CONTEXT } from '@/lib/playbookScript';

/**
 * [v1.1.0] Tokens permitidos no TÍTULO de um step de playbook.
 * Subset alinhado à função SQL `public.render_playbook_step_title` — qualquer
 * token fora desta lista NÃO será interpolado no servidor e ficará literal.
 */
const STEP_TITLE_ALLOWED_TOKENS = [
  'contact.first_name',
  'contact.full_name',
  'contact.phone',
  'contact.email',
  'deal.title',
  'deal.value',
  'deal.phase',
  'deal.pipeline',
  'deal.source',
  'deal.expected_close_date',
  'seller.first_name',
  'seller.full_name',
  'tenant.name',
  'today',
  'tomorrow',
  'weekday',
  'now',
];


import { useActivityPlaybook } from '@/hooks/useActivityPlaybook';
import {
  useActivityPlaybooks,
  type UpsertPlaybookInput,
  type UpsertPlaybookStepInput,
  type PlaybookScope,
} from '@/hooks/useActivityPlaybooks';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { useWorkspacePipelines } from '@/hooks/useWorkspacePipelines';
import { useWorkspacePlaybookBindings, type CadenceTrigger } from '@/hooks/useWorkspacePlaybookBindings';
import { usePlaybookTriggers, type PlaybookTriggerDraft } from '@/hooks/usePlaybookTriggers';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';

const logger = createLogger('CrmPlaybookEditor');

// ============================================================================
// Constantes — opções dos selects
// ============================================================================
const ACTIVITY_TYPES: { value: string; label: string; icon: any }[] = [
  { value: 'call',     label: 'Ligação',  icon: Phone },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'email',    label: 'E-mail',   icon: Mail },
  { value: 'meeting',  label: 'Reunião',  icon: CalendarIcon },
  { value: 'visit',    label: 'Visita',   icon: MapPin },
  { value: 'note',     label: 'Anotação', icon: StickyNote },
  { value: 'task',     label: 'Lembrete', icon: CheckSquare },
];

/**
 * Paleta de hover por tipo de atividade — reflete a mesma linguagem visual
 * do dock do `CampaignMessageBuilder`. Cada tipo tem sua cor de identidade
 * para facilitar reconhecimento no dock lateral.
 */
const ACTIVITY_TYPE_HOVER: Record<string, string> = {
  call:     'hover:text-emerald-500 hover:border-emerald-400/60',
  whatsapp: 'hover:text-green-500 hover:border-green-400/60',
  email:    'hover:text-sky-500 hover:border-sky-400/60',
  meeting:  'hover:text-indigo-500 hover:border-indigo-400/60',
  visit:    'hover:text-orange-500 hover:border-orange-400/60',
  note:     'hover:text-amber-500 hover:border-amber-400/60',
  task:     'hover:text-violet-500 hover:border-violet-400/60',
};

const OFFSET_UNITS = [
  { value: 'minutes',       label: 'Minutos' },
  { value: 'hours',         label: 'Horas' },
  { value: 'days',          label: 'Dias' },
  { value: 'business_days', label: 'Dias úteis' },
];

// Anchors aplicáveis por scope:
// - flat: cadence_start, previous_step_completed, deal_created
// - per_phase: phase_entered (default), previous_step_completed
// (cadence_start e deal_created não fazem sentido em per_phase porque steps
//  são gerados ao entrar na fase; phase_entered não faz sentido em flat)
const ANCHORS_FLAT = [
  { value: 'cadence_start',           label: 'Início da cadência' },
  { value: 'previous_step_completed', label: 'Após step anterior' },
  { value: 'deal_created',            label: 'Criação do negócio' },
];
const ANCHORS_PER_PHASE = [
  { value: 'phase_entered',           label: 'Entrada na fase' },
  { value: 'previous_step_completed', label: 'Após step anterior' },
];
const ANCHOR_LABELS: Record<string, string> = {
  cadence_start: 'Início da cadência',
  previous_step_completed: 'Após step anterior',
  deal_created: 'Criação do negócio',
  phase_entered: 'Entrada na fase',
};

// ============================================================================
// Tipo do step em edição (com chave estável local)
// ============================================================================
interface EditableStep extends UpsertPlaybookStepInput {
  _key: string; // chave estável para drag-and-drop e React keys
  /** UUID real do step no banco (ausente em steps recém-criados) */
  id?: string | null;
  activity_type: string;
  title: string;
  due_offset_value: number;
  due_offset_unit: 'minutes' | 'hours' | 'days' | 'business_days';
  due_anchor: 'cadence_start' | 'previous_step_completed' | 'deal_created' | 'phase_entered';
  is_required: boolean;
  /** Em modo per_phase, fase a que o step pertence (obrigatório) */
  phase_id?: string | null;
}

// Cria step novo com defaults; recebe phase_id opcional p/ pré-selecionar a fase
function makeNewStep(phaseId?: string | null): EditableStep {
  return {
    _key: crypto.randomUUID(),
    activity_type: 'call',
    title: '',
    description_html: '',
    channel: null,
    due_offset_value: 1,
    due_offset_unit: 'days',
    due_anchor: phaseId ? 'phase_entered' : 'cadence_start',
    is_required: true,
    sla_hours: null,
    auto_complete_on: null,
    phase_id: phaseId ?? null,
    // Roteiro (Playbook v2.0)
    script_html: null,
    goal: null,
    success_criteria: [],
    objections: [],
    script_variables_used: [],
  };
}

// ============================================================================
// Componente principal
// ============================================================================
export default function CrmPlaybookEditor() {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const isNew = !routeId || routeId === 'new';
  const playbookId = isNew ? null : routeId!;
  const queryWorkspaceId = search.get('workspace_id');

  // --------------------------------------------------------------------------
  // Carregamento
  // --------------------------------------------------------------------------
  const {
    data: details,
    isLoading,
    error,
  } = useActivityPlaybook(playbookId);

  // Mutations vêm do hook de listagem (workspaceId pode mudar via Select em criação livre)
  const { upsertPlaybook, isUpserting } = useActivityPlaybooks(
    isNew ? queryWorkspaceId : details?.playbook?.workspace_id ?? null,
  );

  // --------------------------------------------------------------------------
  // Estado local — formulário do header + steps
  // --------------------------------------------------------------------------
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#6366f1');
  const [isActive, setIsActive] = useState(true);
  // Modal de ajuda contextual sobre regras do playbook e anchors
  const [helpOpen, setHelpOpen] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  // [v2.1.0] Permissões do vendedor sobre steps da cadência
  const [allowSellerCancelStep, setAllowSellerCancelStep] = useState(false);
  const [allowSellerCreateManualStep, setAllowSellerCreateManualStep] = useState(false);
  const [steps, setSteps] = useState<EditableStep[]>([]);
  // Workspace vinculado — editável apenas em criação SEM query param.
  const [workspaceId, setWorkspaceIdState] = useState<string | null>(
    queryWorkspaceId,
  );

  // Modo de organização do playbook
  const [scope, setScope] = useState<PlaybookScope>('flat');
  // Pipeline alvo (obrigatório quando scope=per_phase)
  const [pipelineId, setPipelineId] = useState<string | null>(null);

  // Lista de workspaces disponíveis para o select (apenas em criação livre)
  const { workspaces = [] } = useCrmWorkspacesHierarchy();

  // Pipelines do workspace selecionado (para escolher o pipeline alvo do per_phase)
  const { data: workspacePipelines = [] } = useWorkspacePipelines(workspaceId, {
    enabled: !!workspaceId,
  });

  // Fases do pipeline alvo selecionado (usado para agrupar/atribuir steps)
  const targetPipelinePhases = useMemo(() => {
    if (!pipelineId) return [];
    return workspacePipelines.find((p) => p.id === pipelineId)?.phases ?? [];
  }, [pipelineId, workspacePipelines]);



  // Regra de bloqueio do select de workspace
  const isWorkspaceLocked = !isNew || !!queryWorkspaceId;

  // --------------------------------------------------------------------------
  // Gatilhos (bindings) do playbook — v1.1.0
  // Reaproveita as RPCs `list/upsert/delete_pipeline_playbook_binding`
  // via useWorkspacePlaybookBindings (listagem) + usePlaybookTriggers (diff/save).
  // --------------------------------------------------------------------------
  const bindingsWorkspaceId = isNew
    ? workspaceId
    : details?.playbook?.workspace_id ?? null;
  const { bindings: workspaceBindings } = useWorkspacePlaybookBindings(bindingsWorkspaceId);
  const { saveTriggers, isSaving: isSavingTriggers } = usePlaybookTriggers();

  // --------------------------------------------------------------------------
  // [v1.2.0] Workspace: flag `playbook_mode_enabled`
  //
  // O trigger `trg_auto_start_deal_cadences` (banco) faz curto-circuito
  // quando esse flag está desligado — nenhuma cadência é criada e a aba
  // "Cadência" não aparece no modal do negócio. Aqui apenas SINALIZAMOS ao
  // usuário e oferecemos um botão para ativar (mesma mutation usada em
  // `WorkspacePlaybooksTab.tsx`).
  // --------------------------------------------------------------------------
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const playbookModeQuery = useQuery({
    queryKey: ['crm-workspace-playbook-mode', bindingsWorkspaceId, currentTenant?.id],
    enabled: !!bindingsWorkspaceId && !!currentTenant?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from('crm_workspaces')
        .select('playbook_mode_enabled')
        .eq('id', bindingsWorkspaceId as string)
        .maybeSingle();
      if (error) throw error;
      return !!data?.playbook_mode_enabled;
    },
  });
  const enablePlaybookModeMutation = useMutation({
    mutationFn: async () => {
      if (!bindingsWorkspaceId || !currentTenant?.id) {
        throw new Error('Workspace ou tenant indisponível');
      }
      const { error } = await supabase
        .from('crm_workspaces')
        .update({ playbook_mode_enabled: true })
        .eq('id', bindingsWorkspaceId)
        .eq('tenant_id', currentTenant.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crm-workspace-playbook-mode', bindingsWorkspaceId],
      });
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      toast.success('Modo Playbook ativado neste workspace');
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Erro ao ativar Modo Playbook');
    },
  });
  const showPlaybookModeWarning =
    !!bindingsWorkspaceId && playbookModeQuery.data === false;

  // Rascunho local dos gatilhos deste playbook (filtrados do workspace)
  const [triggers, setTriggers] = useState<PlaybookTriggerDraft[]>([]);
  // Snapshot dos bindings originais (para calcular remoções no save)
  const [originalTriggerIds, setOriginalTriggerIds] = useState<string[]>([]);

  // Hidrata gatilhos quando bindings do workspace chegam e playbook_id existe
  useEffect(() => {
    if (!playbookId) return;
    const mine = workspaceBindings.filter((b) => b.playbook_id === playbookId);
    setTriggers(
      mine.map((b) => ({
        id: b.id,
        workspace_id: b.workspace_id,
        playbook_id: b.playbook_id,
        pipeline_id: b.pipeline_id,
        phase_id: b.phase_id,
        trigger: b.trigger,
        cancel_previous: b.cancel_previous,
        is_active: b.is_active,
        sort_order: b.sort_order,
      })),
    );
    setOriginalTriggerIds(mine.map((b) => b.id));
  }, [workspaceBindings, playbookId]);

  // --------------------------------------------------------------------------
  // Sincronização: quando o playbook está em modo `per_phase`, todos os
  // gatilhos herdam automaticamente o pipeline alvo (evita confusão com o
  // seletor "Pipeline (opcional)" no card do gatilho — v1.2.0).
  // Se o `phase_id` pertence a outro pipeline, é resetado para forçar
  // re-seleção dentro do pipeline correto.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (scope !== 'per_phase' || !pipelineId) return;
    const validPhaseIds = new Set(
      (workspacePipelines.find((p) => p.id === pipelineId)?.phases ?? []).map(
        (ph: any) => ph.id,
      ),
    );
    setTriggers((prev) => {
      let changed = false;
      const next = prev.map((t) => {
        const needsPipelineFix = t.pipeline_id !== pipelineId;
        const needsPhaseReset =
          !!t.phase_id && !validPhaseIds.has(t.phase_id);
        if (needsPipelineFix || needsPhaseReset) {
          changed = true;
          return {
            ...t,
            pipeline_id: pipelineId,
            phase_id: needsPhaseReset ? null : t.phase_id,
          };
        }
        return t;
      });
      return changed ? next : prev;
    });
  }, [scope, pipelineId, workspacePipelines]);



  // Hidrata estado quando details chega
  useEffect(() => {
    if (!details) return;
    const p = details.playbook as any;
    setName(p.name);
    setDescription(p.description ?? '');
    setColor(p.color ?? '#6366f1');
    setIsActive(p.is_active);
    setIsDefault(p.is_default);
    setWorkspaceIdState(p.workspace_id);
    setScope((p.scope as PlaybookScope) ?? 'flat');
    setPipelineId(p.pipeline_id ?? null);
    // [v2.1.0] Hidrata flags de permissão do vendedor
    setAllowSellerCancelStep(!!p.allow_seller_cancel_step);
    setAllowSellerCreateManualStep(!!p.allow_seller_create_manual_step);
    // Hidrata steps preservando _key locais quando a contagem bate
    // (caso típico de refetch pós-save). Trocar _key faz StepRow desmontar e
    // quebra dnd-kit + portais do Sheet (NotFoundError em removeChild).
    setSteps((prev) => {
      const incoming = details.steps
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order);

      const mapped = incoming.map((s) => ({
        // Preserva o id real do banco para que o backend faça UPDATE
        // do step (em vez de DELETE+INSERT) e cadências ativas mantenham
        // o vínculo via playbook_step_id (Live Edit Sempre — v2.1.0).
        id: s.id,
        activity_type: s.activity_type,
        title: s.title,
        description_html: s.description_html ?? '',
        channel: s.channel,
        due_offset_value: s.due_offset_value,
        due_offset_unit: s.due_offset_unit as any,
        due_anchor: s.due_anchor as any,
        is_required: s.is_required,
        sla_hours: s.sla_hours,
        auto_complete_on: s.auto_complete_on,
        phase_id: (s as any).phase_id ?? null,
        // Roteiro (Playbook v2.0)
        script_html: (s as any).script_html ?? null,
        goal: (s as any).goal ?? null,
        success_criteria: (s as any).success_criteria ?? [],
        objections: (s as any).objections ?? [],
        script_variables_used: (s as any).script_variables_used ?? [],
      }));

      // Preserva identidade React quando a estrutura é idêntica
      if (prev.length === mapped.length && prev.length > 0) {
        return mapped.map((m, i) => ({ ...m, _key: prev[i]._key }));
      }
      // Carga inicial / mudança estrutural: usa id real do banco
      return mapped.map((m, i) => ({ ...m, _key: incoming[i].id }));
    });
  }, [details]);

  // --------------------------------------------------------------------------
  // Drag-and-drop
  // --------------------------------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Drag-and-drop no modo flat: reordena o array global de steps.
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps((prev) => {
      const oldIndex = prev.findIndex((s) => s._key === active.id);
      const newIndex = prev.findIndex((s) => s._key === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // Drag-and-drop no modo per_phase: reordena APENAS dentro da fase indicada,
  // depois recompõe o array global preservando a posição relativa das demais
  // fases. Como `sort_order` é recalculado por idx global no save (handleSave),
  // basta garantir que steps da mesma fase fiquem na ordem desejada.
  const handleDragEndInPhase = (phaseId: string) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps((prev) => {
      // Subarray da fase, com índice global de cada step para reinserção.
      const phaseEntries = prev
        .map((s, i) => ({ s, i }))
        .filter((e) => e.s.phase_id === phaseId);
      const oldLocal = phaseEntries.findIndex((e) => e.s._key === active.id);
      const newLocal = phaseEntries.findIndex((e) => e.s._key === over.id);
      if (oldLocal < 0 || newLocal < 0) return prev;

      const reorderedPhase = arrayMove(
        phaseEntries.map((e) => e.s),
        oldLocal,
        newLocal,
      );
      // Reescreve as posições globais ocupadas pela fase com a nova ordem,
      // mantendo intactos os steps de outras fases.
      const next = [...prev];
      phaseEntries.forEach((entry, idx) => {
        next[entry.i] = reorderedPhase[idx];
      });
      return next;
    });
  };

  // --------------------------------------------------------------------------
  // Helpers de manipulação de steps
  // --------------------------------------------------------------------------
  const updateStep = (key: string, patch: Partial<EditableStep>) => {
    setSteps((prev) =>
      prev.map((s) => (s._key === key ? { ...s, ...patch } : s)),
    );
  };
  const removeStep = (key: string) => {
    setSteps((prev) => prev.filter((s) => s._key !== key));
  };
  // Em per_phase, opcionalmente recebe uma fase para já vincular o novo step
  // Em per_phase, opcionalmente recebe uma fase para já vincular o novo step.
  // Também aceita `activityType` para permitir que o dock de tipos crie steps
  // já com o tipo escolhido (padrão UX do CampaignMessageBuilder).
  const addStep = (phaseId?: string | null, activityType?: string) => {
    setSteps((prev) => {
      const s = makeNewStep(phaseId);
      if (activityType) s.activity_type = activityType;
      return [...prev, s];
    });
  };

  // --------------------------------------------------------------------------
  // Preview de timeline (estimativa visual)
  // --------------------------------------------------------------------------
  const timeline = useMemo(() => {
    // Calcula um deslocamento aproximado em minutos a partir do "início" virtual
    // — apenas para preview; o cálculo real ocorre no backend ao iniciar a
    //   cadência.
    let cumulativeMinutes = 0;
    return steps.map((s, idx) => {
      const minutes = toMinutes(s.due_offset_value, s.due_offset_unit);
      // Só "previous_step_completed" acumula; demais ancoram no início virtual.
      const offsetFromStart =
        s.due_anchor === 'previous_step_completed'
          ? cumulativeMinutes + minutes
          : minutes;
      cumulativeMinutes = offsetFromStart;
      return {
        index: idx,
        key: s._key,
        title: s.title || `Step ${idx + 1}`,
        type: s.activity_type,
        anchor: s.due_anchor,
        offsetLabel: humanOffset(offsetFromStart),
      };
    });
  }, [steps]);

  // --------------------------------------------------------------------------
  // Salvar
  // --------------------------------------------------------------------------
  const handleSave = async () => {
    // Validação básica
    if (!name.trim()) {
      toast.error('Informe o nome do playbook.');
      return;
    }
    // Em edição, sempre usa o workspace original (vínculo permanente).
    // Em criação, usa o workspace selecionado no Select (que pode vir do query param).
    const effectiveWorkspaceId = isNew
      ? workspaceId
      : details?.playbook?.workspace_id ?? null;
    if (!effectiveWorkspaceId) {
      toast.error('Selecione um workspace para vincular o playbook.');
      return;
    }
    // Em per_phase, pipeline_id é obrigatório e cada step precisa de phase_id
    if (scope === 'per_phase') {
      if (!pipelineId) {
        toast.error('Selecione o pipeline alvo para o playbook organizado por fase.');
        return;
      }
    }
    for (const [i, s] of steps.entries()) {
      if (!s.title.trim()) {
        toast.error(`Step ${i + 1}: título é obrigatório.`);
        return;
      }
      if (scope === 'per_phase' && !s.phase_id) {
        toast.error(`Step ${i + 1}: selecione a fase do pipeline.`);
        return;
      }
    }

    const payload: UpsertPlaybookInput = {
      id: isNew ? null : playbookId,
      workspace_id: effectiveWorkspaceId,
      name: name.trim(),
      description: description.trim() || null,
      color,
      is_active: isActive,
      is_default: isDefault,
      scope,
      pipeline_id: scope === 'per_phase' ? pipelineId : null,
      // [v2.1.0] Flags de permissão do vendedor
      allow_seller_cancel_step: allowSellerCancelStep,
      allow_seller_create_manual_step: allowSellerCreateManualStep,
      steps: steps.map((s, idx) => ({
        // Envia id apenas para steps já existentes no banco; novos vão sem id
        // e são INSERT pelo backend. Steps existentes vão por UPDATE,
        // preservando playbook_step_id nas cadências em andamento.
        ...(s.id ? { id: s.id } : {}),
        sort_order: idx,
        activity_type: s.activity_type,
        title: s.title.trim(),
        description_html: s.description_html ?? null,
        channel: s.channel,
        due_offset_value: s.due_offset_value,
        due_offset_unit: s.due_offset_unit,
        due_anchor: s.due_anchor,
        is_required: s.is_required,
        sla_hours: s.sla_hours,
        auto_complete_on: s.auto_complete_on,
        phase_id: scope === 'per_phase' ? s.phase_id ?? null : null,
        // Roteiro (Playbook v2.0)
        script_html: s.script_html ?? null,
        goal: s.goal ?? null,
        success_criteria: s.success_criteria ?? [],
        objections: s.objections ?? [],
        script_variables_used: s.script_variables_used ?? [],
      })),
    };

    // Validação de gatilhos: deal_enters_phase exige phase_id
    for (const [i, tr] of triggers.entries()) {
      if (tr.trigger === 'deal_enters_phase' && !tr.phase_id) {
        toast.error(`Gatilho ${i + 1}: selecione a fase para "Ao entrar em uma fase".`);
        return;
      }
    }

    try {
      const newId = await upsertPlaybook(payload);

      // Persiste gatilhos sequencialmente (evita deadlock, mesma lição do
      // handleInfoSave — DealDetailsModalPremium). Só executa se houve
      // criação ou alteração real na lista de gatilhos.
      const effectivePlaybookId = newId ?? playbookId;
      if (effectivePlaybookId && effectiveWorkspaceId) {
        try {
          await saveTriggers({
            playbookId: effectivePlaybookId,
            workspaceId: effectiveWorkspaceId,
            current: originalTriggerIds.map((id) => ({ id })),
            desired: triggers.map((t, idx) => ({
              ...t,
              playbook_id: effectivePlaybookId,
              workspace_id: effectiveWorkspaceId,
              // Guarda defensiva: em per_phase o gatilho DEVE herdar o
              // pipeline alvo (redundante com o useEffect de sincronização,
              // porém evita persistir estado obsoleto caso o React ainda
              // não tenha aplicado o efeito antes do submit — v1.2.0).
              pipeline_id: scope === 'per_phase' ? pipelineId : t.pipeline_id,
              sort_order: idx,
            })),
          });
        } catch (err: any) {
          logger.error('Erro ao salvar gatilhos', err);
          toast.error(err?.message ?? 'Playbook salvo, mas houve erro ao salvar os gatilhos.');
        }
      }

      toast.success('Playbook de Vendas salvo com sucesso.');
      if (isNew) {
        navigate(`/tenant/crm/playbooks/${newId}`, { replace: true });
      }
    } catch (err: any) {
      logger.error('Erro ao salvar playbook', err);
      toast.error(err?.message ?? 'Não foi possível salvar o playbook.');
    }
  };

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  if (!isNew && isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-12 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isNew && error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center text-destructive">
            Erro ao carregar playbook: {(error as Error).message}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-background sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tenant/crm/playbooks')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Voltar
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <h1 className="text-lg font-semibold truncate">
            {isNew ? 'Novo Playbook de Vendas' : name || 'Playbook de Vendas'}
          </h1>
          {!isActive && <Badge variant="outline">Inativo</Badge>}
          {isDefault && <Badge variant="secondary">Padrão</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {/* Botão de ajuda — abre modal explicando regras do playbook e anchors */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHelpOpen(true)}
            title="Como funciona o Playbook de Vendas"
          >
            <HelpCircle className="h-4 w-4 mr-1" />
            Ajuda
          </Button>
          <Button onClick={handleSave} disabled={isUpserting || isSavingTriggers}>
            {isUpserting || isSavingTriggers ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Salvar
          </Button>
        </div>
      </div>

      {/* Modal de ajuda */}
      <PlaybookHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />

      {/* [v1.2.0] Aviso: workspace com "Modo Playbook" desligado.
          Sem esse flag, o trigger de banco não inicia cadências automáticas,
          e a aba "Cadência" nunca aparece no modal do negócio. */}
      {showPlaybookModeWarning && (
        <div className="px-4 pt-3">
          <InlineNotice variant="warning" icon={AlertTriangle}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <div className="font-medium">
                  Modo Playbook está desativado neste workspace
                </div>
                <div className="text-muted-foreground text-xs mt-0.5">
                  Enquanto essa opção estiver desligada, os gatilhos deste
                  playbook não iniciam cadências automaticamente ao criar/mover
                  negócios — e a aba <strong>Cadência</strong> não aparece no
                  modal do negócio.
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => enablePlaybookModeMutation.mutate()}
                disabled={enablePlaybookModeMutation.isPending}
                className="shrink-0"
              >
                {enablePlaybookModeMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : null}
                Ativar Modo Playbook
              </Button>
            </div>
          </InlineNotice>
        </div>
      )}




      {/* Body — grid 12 cols (steps 8 / timeline 4) */}
      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-4">
          {/* Coluna principal — header + steps */}
          <div className="lg:col-span-8">
            <Tabs defaultValue="general" className="space-y-4">
              <TabsList className="grid grid-cols-2 w-full max-w-md">
                <TabsTrigger value="general">Geral</TabsTrigger>
                <TabsTrigger value="triggers" className="gap-1.5">
                  <Zap className="h-3.5 w-3.5" />
                  Gatilhos ({triggers.length})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="general" className="space-y-4 mt-0">
            <Card>

              <CardHeader className="pb-3">
                <CardTitle className="text-base">Informações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Workspace — bloqueado em edição ou quando vem do query param */}
                <div className="space-y-1.5">
                  <Label>
                    Workspace *{' '}
                    {isWorkspaceLocked && (
                      <span className="text-xs font-normal text-muted-foreground">
                        (vínculo permanente — não pode ser alterado)
                      </span>
                    )}
                  </Label>
                  <Select
                    value={workspaceId ?? undefined}
                    onValueChange={(v) => setWorkspaceIdState(v)}
                    disabled={isWorkspaceLocked}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o workspace" />
                    </SelectTrigger>
                    <SelectContent>
                      {workspaces.map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name}
                        </SelectItem>
                      ))}
                      {/* Garante que o workspace atual apareça mesmo se não estiver na lista carregada */}
                      {workspaceId &&
                        !workspaces.some((w) => w.id === workspaceId) &&
                        details?.playbook && (
                          <SelectItem value={workspaceId}>
                            {(details.playbook as any).workspace_name ??
                              'Workspace atual'}
                          </SelectItem>
                        )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-3">
                  <div className="space-y-1.5">
                    <Label>Nome *</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ex.: Cadência de Outbound"
                      maxLength={120}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Cor</Label>
                    <Input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="h-10 p-1"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Descrição</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Quando e por que usar este playbook"
                    rows={2}
                    maxLength={500}
                  />
                </div>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    {/* Toggle padronizado (Switch unificado, cor emerald) */}
                    <Switch checked={isActive} onCheckedChange={setIsActive} />
                    <Label>Ativo</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={isDefault} onCheckedChange={setIsDefault} />
                    <Label>Padrão do workspace</Label>
                  </div>

                  {/* [v2.1.0] Permissões do vendedor sobre steps da cadência */}
                  <div className="flex items-center gap-2" title="Se ativo, o vendedor pode cancelar um step preservando o histórico. Admin/supervisor sempre podem.">
                    <Switch
                      checked={allowSellerCancelStep}
                      onCheckedChange={setAllowSellerCancelStep}
                    />
                    <Label>Vendedor pode cancelar step</Label>
                  </div>
                  <div className="flex items-center gap-2" title="Se ativo, o vendedor pode adicionar steps manuais ad-hoc à cadência. Steps manuais não impactam métricas de aderência ao playbook.">
                    <Switch
                      checked={allowSellerCreateManualStep}
                      onCheckedChange={setAllowSellerCreateManualStep}
                    />
                    <Label>Vendedor pode criar step manual</Label>
                  </div>
                </div>

                {/* ============================================================
                    Modo de organização (flat vs per_phase) — seletor visual
                    limpo (2 cards clean, sem fundo cinza pesado).
                   ============================================================ */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Modo de organização</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {[
                      {
                        value: 'flat' as PlaybookScope,
                        icon: Rows3,
                        title: 'Fluxo único',
                        desc: 'Uma sequência linear de passos aplicada ao negócio inteiro.',
                      },
                      {
                        value: 'per_phase' as PlaybookScope,
                        icon: Columns3,
                        title: 'Por fase do pipeline',
                        desc: 'Cada fase tem seus próprios passos, injetados ao entrar nela.',
                      },
                    ].map((opt) => {
                      const selected = scope === opt.value;
                      const Icon = opt.icon;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            const newScope = opt.value;
                            if (newScope === scope) return;
                            setScope(newScope);
                            if (newScope === 'flat') {
                              setPipelineId(null);
                              setSteps((prev) =>
                                prev.map((s) => ({ ...s, phase_id: null })),
                              );
                            }
                          }}
                          className={cn(
                            'group relative text-left rounded-xl border p-3.5 transition-all',
                            'hover:border-primary/40 hover:shadow-sm',
                            selected
                              ? 'border-primary ring-1 ring-primary/20 bg-primary/[0.03]'
                              : 'border-border bg-background',
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={cn(
                                'shrink-0 rounded-lg p-2 transition-colors',
                                selected
                                  ? 'bg-primary/10 text-primary'
                                  : 'bg-muted text-muted-foreground group-hover:text-primary',
                              )}
                            >
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold">{opt.title}</div>
                              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                {opt.desc}
                              </p>
                            </div>
                            {selected && (
                              <Check className="h-4 w-4 text-primary shrink-0" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {scope === 'per_phase' && (
                    <div className="space-y-1.5 pt-1">
                      <Label>Pipeline alvo *</Label>
                      <Select
                        value={pipelineId ?? undefined}
                        onValueChange={(v) => {
                          setPipelineId(v);
                          // Ao trocar de pipeline, limpa phase_id dos steps
                          setSteps((prev) =>
                            prev.map((s) => ({ ...s, phase_id: null })),
                          );
                        }}
                        disabled={!workspaceId}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              workspaceId
                                ? 'Selecione o pipeline'
                                : 'Selecione um workspace primeiro'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {workspacePipelines.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Steps — modo flat (layout Studio: dock lateral + timeline canvas) */}
            {scope === 'flat' && (
              <Card>
                <CardHeader className="pb-3 flex-row items-center justify-between">
                  <CardTitle className="text-base">
                    Passos ({steps.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <PlaybookStepsStudio
                    steps={steps}
                    allSteps={steps}
                    scope={scope}
                    sensors={sensors}
                    onDragEnd={handleDragEnd}
                    onAdd={(activityType) => addStep(null, activityType)}
                    onUpdate={(key, patch) => updateStep(key, patch)}
                    onRemove={(key) => removeStep(key)}
                    onOpenHelp={() => setHelpOpen(true)}
                  />
                </CardContent>
              </Card>
            )}

            {/* Steps — modo per_phase: blocos individuais suspensos por fase
                (sem wrapper Card externo, cada fase é seu próprio bloco).
             */}
            {scope === 'per_phase' && (
              <div className="space-y-3">
                {!pipelineId ? (
                  <Card>
                    <CardContent className="text-center py-6 text-sm text-muted-foreground">
                      Selecione o pipeline alvo na aba Geral para configurar os
                      passos por fase.
                    </CardContent>
                  </Card>
                ) : targetPipelinePhases.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-6 text-sm text-muted-foreground">
                      Este pipeline não possui fases.
                    </CardContent>
                  </Card>
                ) : (
                  targetPipelinePhases.map((phase) => {
                    const phaseSteps = steps.filter((s) => s.phase_id === phase.id);
                    return (
                      <Card key={phase.id} className="shadow-sm">
                        <CardHeader className="pb-2 flex-row items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="h-2.5 w-2.5 rounded-full shrink-0"
                              style={{
                                backgroundColor:
                                  phase.color || 'hsl(var(--primary))',
                              }}
                            />
                            <CardTitle className="text-base truncate">
                              {phase.name}
                            </CardTitle>
                            <Badge
                              variant="secondary"
                              className="text-[10px] h-4 px-1.5"
                            >
                              {phaseSteps.length}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <PlaybookStepsStudio
                            steps={phaseSteps}
                            allSteps={steps}
                            scope={scope}
                            sensors={sensors}
                            onDragEnd={handleDragEndInPhase(phase.id)}
                            onAdd={(activityType) => addStep(phase.id, activityType)}
                            onUpdate={(key, patch) => updateStep(key, patch)}
                            onRemove={(key) => removeStep(key)}
                            onOpenHelp={() => setHelpOpen(true)}
                            compact
                          />
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            )}
              </TabsContent>

              {/* ==============================================================
                  Aba Gatilhos — regras de disparo automático (v1.1.0)
                 ============================================================== */}
              <TabsContent value="triggers" className="space-y-4 mt-0">
                <Card>
                  <CardHeader className="pb-3 flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Zap className="h-4 w-4" />
                        Gatilhos de disparo ({triggers.length})
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        Quando este playbook deve ser iniciado automaticamente.
                        Você pode adicionar múltiplos gatilhos (ex.: ao criar e
                        também manual).
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const effectiveWs = bindingsWorkspaceId ?? workspaceId ?? '';
                        setTriggers((prev) => [
                          ...prev,
                          {
                            id: null,
                            workspace_id: effectiveWs,
                            playbook_id: playbookId ?? '',
                            pipeline_id: scope === 'per_phase' ? pipelineId : null,
                            phase_id: null,
                            trigger: 'manual',
                            cancel_previous: false,
                            is_active: true,
                            sort_order: prev.length,
                          },
                        ]);
                      }}
                      disabled={!bindingsWorkspaceId && !workspaceId}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar gatilho
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {isNew && !playbookId && (
                      <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 text-[11px] text-amber-700 dark:text-amber-400 mb-3">
                        Você pode configurar os gatilhos agora — eles serão salvos
                        junto com o playbook ao clicar em <strong>Salvar</strong>.
                      </div>
                    )}
                    {triggers.length === 0 ? (
                      <div className="text-center py-6 text-sm text-muted-foreground">
                        Nenhum gatilho configurado. Sem gatilhos, este playbook só
                        inicia manualmente pelo botão <strong>Raio</strong> no negócio.
                      </div>
                    ) : (
                      <div className="grid gap-2">
                        {triggers.map((tr, idx) => {
                          const pipeline = workspacePipelines.find((p) => p.id === tr.pipeline_id);
                          const phases = pipeline?.phases ?? [];
                          return (
                            <div
                              key={idx}
                              className="rounded-md border bg-background p-3 space-y-2"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-muted-foreground">
                                  Gatilho #{idx + 1}
                                </span>
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1.5">
                                    <Label className="text-[11px] text-muted-foreground">Ativo</Label>
                                    <Switch
                                      checked={tr.is_active}
                                      onCheckedChange={(checked) =>
                                        setTriggers((prev) =>
                                          prev.map((t, i) =>
                                            i === idx ? { ...t, is_active: checked } : t,
                                          ),
                                        )
                                      }
                                    />
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-destructive hover:text-destructive"
                                    onClick={() =>
                                      setTriggers((prev) => prev.filter((_, i) => i !== idx))
                                    }
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>

                              <div
                                className={
                                  scope === 'per_phase'
                                    ? 'grid grid-cols-1 gap-2'
                                    : 'grid grid-cols-1 md:grid-cols-2 gap-2'
                                }
                              >
                                <div className="space-y-1">
                                  <Label className="text-[11px]">Quando disparar</Label>
                                  <Select
                                    value={tr.trigger}
                                    onValueChange={(v) =>
                                      setTriggers((prev) =>
                                        prev.map((t, i) =>
                                          i === idx
                                            ? {
                                                ...t,
                                                trigger: v as CadenceTrigger,
                                                phase_id: v === 'deal_enters_phase' ? t.phase_id : null,
                                              }
                                            : t,
                                        ),
                                      )
                                    }
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="deal_created">Ao criar o negócio</SelectItem>
                                      <SelectItem value="deal_enters_phase">
                                        Ao entrar em uma fase
                                      </SelectItem>
                                      <SelectItem value="manual">Disparo manual</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {/* [v1.2.0] Texto auxiliar: reforça que
                                      `deal_enters_phase` só dispara na
                                      MUDANÇA de fase — não na criação do
                                      negócio já dentro da fase alvo. */}
                                  {tr.trigger === 'deal_enters_phase' && (
                                    <p className="text-[10.5px] leading-snug text-muted-foreground flex items-start gap-1 pt-0.5">
                                      <Info className="h-3 w-3 mt-[1px] shrink-0" />
                                      <span>
                                        Dispara ao <strong>mover</strong> o
                                        negócio para esta fase. Para acionar
                                        no momento da criação, use{' '}
                                        <em>Ao criar o negócio</em>.
                                      </span>
                                    </p>
                                  )}
                                </div>


                                {scope !== 'per_phase' && (
                                  <div className="space-y-1">
                                    <Label className="text-[11px]">Pipeline (opcional)</Label>
                                    <Select
                                      value={tr.pipeline_id ?? '__all__'}
                                      onValueChange={(v) =>
                                        setTriggers((prev) =>
                                          prev.map((t, i) =>
                                            i === idx
                                              ? {
                                                  ...t,
                                                  pipeline_id: v === '__all__' ? null : v,
                                                  phase_id: null,
                                                }
                                              : t,
                                          ),
                                        )
                                      }
                                    >
                                      <SelectTrigger className="h-8">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="__all__">Todos os pipelines</SelectItem>
                                        {workspacePipelines.map((p) => (
                                          <SelectItem key={p.id} value={p.id}>
                                            {p.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>

                              {tr.trigger === 'deal_enters_phase' && (
                                <div className="space-y-1">
                                  <Label className="text-[11px]">
                                    Fase <span className="text-destructive">*</span>
                                  </Label>
                                  <Select
                                    value={tr.phase_id ?? undefined}
                                    onValueChange={(v) =>
                                      setTriggers((prev) =>
                                        prev.map((t, i) =>
                                          i === idx ? { ...t, phase_id: v } : t,
                                        ),
                                      )
                                    }
                                    disabled={!tr.pipeline_id}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue
                                        placeholder={
                                          tr.pipeline_id
                                            ? scope === 'per_phase'
                                              ? 'Selecione a fase do pipeline alvo'
                                              : 'Selecione a fase'
                                            : 'Selecione um pipeline primeiro'
                                        }
                                      />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {phases.map((ph: any) => (
                                        <SelectItem key={ph.id} value={ph.id}>
                                          {ph.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}

                              <div className="flex items-center justify-between rounded-md border p-2">
                                <Label className="text-[11px]">
                                  Cancelar cadências anteriores ao iniciar esta
                                </Label>
                                <Switch
                                  checked={tr.cancel_previous}
                                  onCheckedChange={(checked) =>
                                    setTriggers((prev) =>
                                      prev.map((t, i) =>
                                        i === idx
                                          ? { ...t, cancel_previous: checked }
                                          : t,
                                      ),
                                    )
                                  }
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>


          {/* Coluna lateral — timeline preview */}
          <div className="lg:col-span-4">
            <Card className="lg:sticky lg:top-20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Linha do tempo
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {timeline.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    Adicione passos para ver a linha do tempo.
                  </p>
                ) : scope === 'per_phase' && pipelineId ? (
                  // Agrupamento por fase — mostra sub-timelines para cada fase
                  // configurada no pipeline alvo (v1.1.0).
                  <div className="space-y-4">
                    {targetPipelinePhases.map((phase) => {
                      const phaseTimeline = timeline.filter((t) => {
                        const step = steps.find((s) => s._key === t.key);
                        return step?.phase_id === phase.id;
                      });
                      if (phaseTimeline.length === 0) return null;
                      return (
                        <div key={phase.id} className="space-y-2">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{
                                backgroundColor:
                                  phase.color || 'hsl(var(--primary))',
                              }}
                            />
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground truncate">
                              {phase.name}
                            </span>
                            <Badge
                              variant="secondary"
                              className="text-[9px] h-4 px-1"
                            >
                              {phaseTimeline.length}
                            </Badge>
                          </div>
                          <ol className="relative border-l border-muted ml-2 space-y-2.5">
                            {phaseTimeline.map((t) => {
                              const meta = ACTIVITY_TYPES.find(
                                (a) => a.value === t.type,
                              );
                              const Icon = meta?.icon ?? CheckSquare;
                              return (
                                <li key={t.key} className="ml-3.5">
                                  <span
                                    className="absolute -left-[7px] flex items-center justify-center h-3.5 w-3.5 rounded-full ring-2 ring-background"
                                    style={{
                                      backgroundColor:
                                        phase.color || color,
                                    }}
                                  >
                                    <Icon className="h-2 w-2 text-white" />
                                  </span>
                                  <div className="text-xs font-medium truncate leading-tight">
                                    {t.title}
                                  </div>
                                  <div className="text-[10px] text-muted-foreground">
                                    {meta?.label ?? t.type} · {t.offsetLabel}
                                  </div>
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <ol className="relative border-l border-muted ml-3 space-y-3">
                    {timeline.map((t) => {
                      const meta = ACTIVITY_TYPES.find((a) => a.value === t.type);
                      const Icon = meta?.icon ?? CheckSquare;
                      return (
                        <li key={t.key} className="ml-4">
                          <span
                            className="absolute -left-[9px] flex items-center justify-center h-4 w-4 rounded-full ring-2 ring-background"
                            style={{ backgroundColor: color }}
                          >
                            <Icon className="h-2.5 w-2.5 text-white" />
                          </span>
                          <div className="text-xs font-semibold truncate">
                            {t.index + 1}. {t.title}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {meta?.label ?? t.type} · {t.offsetLabel}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PlaybookStepsStudio
// ----------------------------------------------------------------------------
// Layout "Studio" inspirado no CampaignMessageBuilder:
//   - Dock lateral esquerdo: paleta de tipos de atividade (clique adiciona step)
//   - Canvas central: timeline vertical com spine, nós numerados e StepRow em
//     modo `timeline` (sem bordas duras, respiro visual, ordem clara).
// Compartilhado pelos modos `flat` (lista única) e `per_phase` (dentro de
// cada bloco de fase, usando prop `compact`).
// ============================================================================
function PlaybookStepsStudio({
  steps,
  allSteps,
  scope,
  sensors,
  onDragEnd,
  onAdd,
  onUpdate,
  onRemove,
  onOpenHelp,
  compact = false,
}: {
  /** Steps visíveis neste canvas (na fase, ou todos em flat). */
  steps: EditableStep[];
  /** Lista global de steps, usada para obter o índice absoluto. */
  allSteps: EditableStep[];
  scope: PlaybookScope;
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (event: DragEndEvent) => void;
  onAdd: (activityType?: string) => void;
  onUpdate: (key: string, patch: Partial<EditableStep>) => void;
  onRemove: (key: string) => void;
  onOpenHelp?: () => void;
  /** Modo compacto para uso dentro dos blocos de fase (per_phase). */
  compact?: boolean;
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex gap-3">
        {/* Dock lateral: tipos de atividade */}
        <div className="flex flex-col items-center gap-1.5 shrink-0 rounded-lg border bg-muted/30 p-1.5">
          {ACTIVITY_TYPES.map((a) => {
            const hover = ACTIVITY_TYPE_HOVER[a.value] ?? 'hover:bg-accent';
            return (
              <Tooltip key={a.value}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onAdd(a.value)}
                    className={cn(
                      'h-9 w-9 rounded-md inline-flex items-center justify-center text-muted-foreground transition-colors',
                      hover,
                    )}
                    aria-label={`Adicionar passo de ${a.label}`}
                  >
                    <a.icon className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{a.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Canvas: timeline */}
        <div className="flex-1 min-w-0 rounded-lg border bg-muted/10 p-3">
          {steps.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              Selecione um tipo de atividade no painel ao lado para adicionar
              o primeiro passo.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={steps.map((s) => s._key)}
                strategy={verticalListSortingStrategy}
              >
                <div className="relative">
                  {/* Spine vertical */}
                  <div
                    className="absolute top-4 bottom-4 w-px bg-border"
                    style={{ left: '15px' }}
                    aria-hidden
                  />
                  <div className="space-y-2">
                    {steps.map((step, localIdx) => {
                      const absoluteIdx = allSteps.indexOf(step);
                      const displayIdx = localIdx + 1;
                      return (
                        <div
                          key={step._key}
                          className="relative flex items-start gap-3"
                        >
                          {/* Nó numerado da timeline */}
                          <div className="relative z-10 shrink-0 mt-1">
                            <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground text-xs font-semibold inline-flex items-center justify-center shadow-sm ring-4 ring-background">
                              {String(displayIdx).padStart(2, '0')}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <StepRow
                              step={step}
                              index={absoluteIdx >= 0 ? absoluteIdx : localIdx}
                              scope={scope}
                              onUpdate={(patch) => onUpdate(step._key, patch)}
                              onRemove={() => onRemove(step._key)}
                              onOpenHelp={onOpenHelp}
                              variant="timeline"
                            />
                          </div>
                        </div>
                      );
                    })}

                    {/* Placeholder final "próximo passo" */}
                    <div className="relative flex items-center gap-3 pt-1">
                      <div className="relative z-10 shrink-0">
                        <div className="h-8 w-8 rounded-full border-2 border-dashed border-border bg-background inline-flex items-center justify-center text-muted-foreground">
                          <Plus className="h-3.5 w-3.5" />
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Próximo passo — escolha um tipo no painel ao lado
                      </span>
                    </div>
                  </div>
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

// ============================================================================
// Linha de step com drag handle (sortable)
// ============================================================================

function StepRow({
  step,
  index,
  scope,
  onUpdate,
  onRemove,
  onOpenHelp,
  variant = 'card',
}: {
  step: EditableStep;
  index: number;
  scope: PlaybookScope;
  onUpdate: (patch: Partial<EditableStep>) => void;
  onRemove: () => void;
  /** Abre o modal completo de ajuda do Playbook (passado pelo container). */
  onOpenHelp?: () => void;
  /** 'card' = padrão antigo; 'timeline' = usado dentro do canvas Studio. */
  variant?: 'card' | 'timeline';
}) {
  const [scriptOpen, setScriptOpen] = useState(false);

  // [v1.1.0] Suporte a variáveis no TÍTULO do step
  const titleInputRef = useRef<HTMLInputElement>(null);

  /** Insere o token `{{...}}` na posição do cursor (ou ao final). */
  const insertTitleToken = (token: string) => {
    const el = titleInputRef.current;
    const snippet = `{{${token}}}`;
    const current = step.title ?? '';
    if (!el) {
      onUpdate({ title: (current + snippet).slice(0, 200) });
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end   = el.selectionEnd   ?? current.length;
    const next = (current.slice(0, start) + snippet + current.slice(end)).slice(0, 200);
    onUpdate({ title: next });
    // Reposiciona cursor após o token inserido
    requestAnimationFrame(() => {
      const pos = Math.min(next.length, start + snippet.length);
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  /** Preview do título usando o contexto fictício compartilhado. */
  const titlePreview = useMemo(
    () => renderScriptAsText(step.title, PREVIEW_CONTEXT) || '—',
    [step.title],
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step._key });

  // Sinaliza visualmente que o step já tem roteiro configurado
  const hasScript = !!(
    step.goal ||
    step.script_html ||
    (step.success_criteria && step.success_criteria.length > 0) ||
    (step.objections && step.objections.length > 0)
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const meta = ACTIVITY_TYPES.find((a) => a.value === step.activity_type);
  const Icon = meta?.icon ?? CheckSquare;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        variant === 'timeline'
          ? cn(
              'rounded-lg p-3 bg-background/60 hover:bg-background transition-colors',
              isDragging && 'shadow-lg ring-2 ring-primary',
            )
          : cn(
              'border rounded-md p-3 bg-card',
              isDragging && 'shadow-lg ring-2 ring-primary',
            ),
        isDragging && 'shadow-lg ring-2 ring-primary',
      )}
    >
      <div className="flex items-start gap-2">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="touch-none mt-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
          aria-label="Reordenar"
        >
          <GripVertical className="h-4 w-4" />
        </button>

        <div className="flex-1 space-y-2 min-w-0">
          {/* Linha 1: índice + tipo + título + remover */}
          <div className="flex items-center gap-2">
            {variant !== 'timeline' && (
              <Badge variant="outline" className="font-mono">
                {index + 1}
              </Badge>
            )}
            <Select
              value={step.activity_type}
              onValueChange={(v) => onUpdate({ activity_type: v })}
            >
              <SelectTrigger className="h-8 w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    <span className="inline-flex items-center gap-2">
                      <a.icon className="h-3.5 w-3.5" />
                      {a.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              ref={titleInputRef}
              value={step.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              placeholder="Título do passo"
              maxLength={200}
              className="h-8 flex-1"
            />
            {/* [v1.1.0] Picker de variáveis para o título — insere token na
                posição do cursor. A interpolação real ocorre no servidor via
                render_playbook_step_title() no momento em que o step é criado. */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0"
                  title="Inserir variável no título"
                  aria-label="Inserir variável no título"
                >
                  <Variable className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" side="bottom" className="w-[420px] p-2">
                <VariablePicker
                  allowedTokens={STEP_TITLE_ALLOWED_TOKENS}
                  onInsert={(token) => insertTitleToken(token)}
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="text-destructive hover:text-destructive h-8 w-8 p-0"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* [v1.1.0] Preview do título interpolado usando o contexto fictício
              — só aparece quando o usuário está de fato usando variáveis. */}
          {step.title.includes('{{') && (
            <p className="text-[11px] text-muted-foreground pl-1">
              Preview: <span className="italic">{titlePreview}</span>
            </p>
          )}


          {/* Linha 2: vencimento (offset + unidade + âncora) */}
          <div className="grid grid-cols-1 md:grid-cols-[100px_120px_1fr] gap-2">
            <Input
              type="number"
              min={0}
              value={step.due_offset_value}
              onChange={(e) =>
                onUpdate({ due_offset_value: Number(e.target.value) || 0 })
              }
              className="h-8"
            />
            <Select
              value={step.due_offset_unit}
              onValueChange={(v: any) => onUpdate({ due_offset_unit: v })}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OFFSET_UNITS.map((u) => (
                  <SelectItem key={u.value} value={u.value}>
                    {u.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Origem do prazo: select + ícone "?" alinhados na mesma altura dos demais campos */}
            <div className="flex items-center gap-1">
              <Select
                value={step.due_anchor}
                onValueChange={(v: any) => onUpdate({ due_anchor: v })}
              >
                <SelectTrigger className="h-8 flex-1" aria-label="Origem do prazo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(scope === 'per_phase' ? ANCHORS_PER_PHASE : ANCHORS_FLAT).map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                  {/* Mantém o anchor legado visível caso esteja fora da lista filtrada */}
                  {!(scope === 'per_phase' ? ANCHORS_PER_PHASE : ANCHORS_FLAT)
                    .some((a) => a.value === step.due_anchor) && (
                    <SelectItem value={step.due_anchor}>
                      {ANCHOR_LABELS[step.due_anchor] ?? step.due_anchor} (legado)
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {/* Ajuda contextual sobre o campo "origem do prazo" */}
              <AnchorHelpPopover scope={scope} onOpenFullHelp={onOpenHelp} />
            </div>
          </div>

          {/* Linha 3: ações do step (roteiro + obrigatório) — mantidas dentro do card */}
          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant={hasScript ? 'secondary' : 'outline'}
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => setScriptOpen(true)}
              title="Configurar roteiro / script"
            >
              <ScrollText className="h-3.5 w-3.5" />
              Roteiro
              {hasScript && (
                <Badge variant="default" className="h-4 px-1 text-[10px] ml-0.5">
                  on
                </Badge>
              )}
            </Button>
            {/* Switch padronizado (mesmo componente da aba Financeiro) */}
            <div className="flex items-center gap-1.5 h-8 px-2">
              <Switch
                checked={step.is_required}
                onCheckedChange={(checked) => onUpdate({ is_required: checked })}
              />
              <span className="text-xs">Obrigatório</span>
            </div>
          </div>
        </div>
      </div>


      {/* Sheet de roteiro do step (Playbook v2.0) */}
      {scriptOpen && (
        <StepScriptSheet
          open={scriptOpen}
          onOpenChange={setScriptOpen}
          step={step}
          stepLabel={`Step ${index + 1} — ${step.title || 'sem título'}`}
          onSave={(patch) => onUpdate(patch as Partial<EditableStep>)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================
function toMinutes(value: number, unit: EditableStep['due_offset_unit']): number {
  switch (unit) {
    case 'minutes':       return value;
    case 'hours':         return value * 60;
    case 'days':          return value * 60 * 24;
    case 'business_days': return value * 60 * 24; // aproximação visual
    default:              return value;
  }
}

function humanOffset(minutes: number): string {
  if (minutes <= 0) return 'imediato';
  if (minutes < 60) return `+${minutes}min`;
  const hours = minutes / 60;
  if (hours < 24) return `+${Math.round(hours)}h`;
  const days = hours / 24;
  return `+${Math.round(days)}d`;
}
